import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import { getDeckAccent } from "../flashcardUtils";

function DeckLibraryPanel({
  decks,
  mode,
  query,
  selectedDeck,
  isSignedIn,
  onModeChange,
  onQueryChange,
  onSelectDeck,
  onCreateDeck,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, lg: 2 },
        borderRadius: "14px",
        bgcolor: "var(--toeic-surface)",
        boxShadow: "var(--toeic-shadow-card)",
        position: { lg: "sticky" },
        top: { lg: 96 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: { xs: 2, lg: 1.5 } }}>
        <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
          <Button
            onClick={() => onModeChange("public")}
            variant={mode === "public" ? "contained" : "outlined"}
            sx={{ borderRadius: "999px", px: { xs: 2, lg: 1.5 } }}
          >
            Public
          </Button>
          <Button
            onClick={() => onModeChange("mine")}
            variant={mode === "mine" ? "contained" : "outlined"}
            disabled={!isSignedIn}
            sx={{ borderRadius: "999px", px: { xs: 2, lg: 1.5 } }}
          >
            My Decks
          </Button>
        </Stack>
        {isSignedIn ? (
          <Tooltip title="Create deck">
            <IconButton onClick={onCreateDeck} sx={{ color: "#6d4aff", bgcolor: "#f0ecff", flex: "0 0 auto" }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            component={Link}
            to="/signin"
            size="small"
            sx={{ borderRadius: "999px", whiteSpace: "nowrap" }}
          >
            Sign in
          </Button>
        )}
      </Stack>
      {!isSignedIn && (
        <Typography
          sx={{
            mb: 2,
            color: "var(--toeic-ink-soft)",
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          Sign in to create decks, save review progress, and open your
          private deck library.
        </Typography>
      )}

      <TextField
        label="Search decks"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        fullWidth
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: "var(--toeic-ink-soft)" }} />,
        }}
      />

      <Box
        className="lingualica-scrollarea"
        sx={{
          mt: 2,
          display: "grid",
          gap: 1,
          maxHeight: { xs: 280, lg: "calc(100dvh - 300px)" },
          overflowY: "auto",
        }}
      >
        {decks.map((deck, index) => (
          <DeckListItem
            key={deck._id}
            deck={deck}
            index={index}
            isSelected={deck._id === selectedDeck?._id}
            onSelect={() => onSelectDeck(deck._id)}
          />
        ))}
        {!decks.length && (
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 14 }}>
            {mode === "mine" ? "No personal decks yet." : "No public decks found."}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function DeckListItem({ deck, index, isSelected, onSelect }) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        "--deck-accent": getDeckAccent(index),
        p: { xs: 1.5, lg: 1.25 },
        borderRadius: "8px",
        border: isSelected
          ? "1.5px solid var(--deck-accent)"
          : "1px solid rgba(215, 234, 245, 0.9)",
        bgcolor: isSelected ? "#faf7ff" : "#fffefd",
        boxShadow: isSelected
          ? "0 12px 26px -22px rgba(19, 43, 63, 0.55)"
          : "0 1px 0 rgba(19, 43, 63, 0.04)",
        cursor: "pointer",
        display: "grid",
        gap: 1.25,
        transition: "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "var(--deck-accent)",
          boxShadow: "0 16px 30px -24px rgba(19, 43, 63, 0.48)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box
          sx={{
            width: { xs: 42, lg: 36 },
            height: { xs: 42, lg: 36 },
            flex: "0 0 auto",
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            bgcolor: "var(--deck-accent)",
            color: "#fff",
            fontSize: { xs: 18, lg: 16 },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          {String(index + 1).padStart(2, "0")}
        </Box>
        <Box
          sx={{
            px: 1.15,
            py: 0.35,
            borderRadius: "999px",
            color: deck.isPublic ? "#248a68" : "var(--toeic-ink-soft)",
            bgcolor: deck.isPublic ? "rgba(79, 191, 159, 0.13)" : "#f3fbff",
          fontSize: { xs: 11, lg: 10.5 },
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {deck.isPublic ? "Public" : "Private"}
        </Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "var(--toeic-ink)", fontWeight: 800, fontSize: { xs: 16, lg: 15 }, lineHeight: 1.2 }}>
          {deck.name}
        </Typography>
        <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: { xs: 13, lg: 12.5 }, mt: 0.45, fontWeight: 500, lineHeight: 1.4 }}>
          {deck.description || "Vocabulary deck"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ color: "var(--toeic-ink)", fontSize: 13, fontWeight: 800 }}>
            {deck.cardCount} cards
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 500 }}>
            by {deck.ownerName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "var(--deck-accent)" }}>
          {deck.isPublic && <PublicIcon sx={{ fontSize: 16 }} />}
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Stack>
      </Box>
    </Box>
  );
}

export default DeckLibraryPanel;
